// The installable desktop / mobile web app (PWA): the browser's install
// prompt (kept for our own "Install app" button), whether we're running
// installed, and the app icon's unread badge.
import { signal } from "@preact/signals";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const installPrompt = signal<InstallPromptEvent | null>(null);
const standalone = () =>
  typeof matchMedia !== "undefined" &&
  (matchMedia("(display-mode: standalone)").matches || matchMedia("(display-mode: window-controls-overlay)").matches || (navigator as { standalone?: boolean }).standalone === true);
export const installed = signal(standalone());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // we show our own button instead of the mini-infobar
    installPrompt.value = e as InstallPromptEvent;
  });
  window.addEventListener("appinstalled", () => {
    installPrompt.value = null;
    installed.value = true;
  });
}

/** Show the browser's install dialog; true if the user installed. */
export async function install(): Promise<boolean> {
  const p = installPrompt.value;
  if (!p) return false;
  await p.prompt();
  const { outcome } = await p.userChoice;
  installPrompt.value = null; // the event can be used once
  return outcome === "accepted";
}

/** The unread badge on the app's icon (installed app; ignored elsewhere). */
export function setBadge(n: number) {
  const nav = navigator as { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (n > 0) void nav.setAppBadge?.(n).catch(() => {});
  else void nav.clearAppBadge?.().catch(() => {});
}

const DISMISS = "xmm.install.dismissed";
export const installDismissed = signal(
  (() => {
    try {
      return Number(localStorage.getItem(DISMISS) ?? 0) > Date.now() - 30 * 86_400_000;
    } catch {
      return false;
    }
  })(),
);
export function dismissInstall() {
  installDismissed.value = true;
  try {
    localStorage.setItem(DISMISS, String(Date.now()));
  } catch {
    /* storage blocked */
  }
}
